import React, { useRef, useReducer, useEffect, useCallback, useMemo } from "react"

import { PRIMARY_BLUE } from "@/constants/colors"

import PlanContainer from "../plan_container"
import { constrainPan, DISABLED_RECOLOR_FILL, DISABLED_RECOLOR_STROKE, paintTable } from "../plan_utils"
import { normalizeSvgInContainer } from "../svg_dom_normalize"
import { type SvgIntrinsicGeometry } from "../svg_geometry"
import { type PlanTable } from "../types"

import { planViewerReducer, createInitialState } from "./plan_viewer_reducer"
import PlanZoomControls from "./plan_zoom_controls"
import { type VenuePlanData } from "./types"

const STYLES = {
  container: "flex flex-col flex-1 h-full gap-2 min-h-0",
  containerMultiPlan: "flex-1 h-full flex flex-col gap-2 min-h-0",
  zoomControlsBar: "flex justify-end px-4 pt-2 flex-shrink-0",
  tabList: "flex items-center justify-between gap-8 px-4 pt-2 flex-shrink-0",
  tabGroup: "flex gap-8",
  tabButton: "px-1 py-2 text-xs border-0 outline-none transition-colors bg-transparent",
  planContent: "flex-1 min-h-0 overflow-hidden flex flex-col",
  flexShrink: "flex-shrink-0",
} as const

type PlanViewerProps = {
  svgContent?: string | null
  venuePlans?: VenuePlanData[]
  tables: PlanTable[]
  selectedTableId?: string | null
  highlightedTableId?: string | null
  selectedTableIds?: string[]
  highlightedTableIds?: string[]
  freeTableIds?: string[]
  disabledTables?: Record<string, string>
  onTableSelect?: (tableId: string, event?: { ctrlKey?: boolean; metaKey?: boolean }) => void
  onClickAway?: () => void
  showUI?: boolean
  showSchedule?: boolean
  scheduleComponent?: React.ReactNode
  showTableLabels?: boolean
  showZoomInTabs?: boolean
  // Section (plan) selection can be driven externally (e.g. a custom mobile tab row). When
  // `activePlanIndex` is provided the built-in tab state is bypassed; pair with `hideSectionTabs`
  // to suppress the built-in tab row entirely. Both default to the current uncontrolled behaviour.
  activePlanIndex?: number
  onActivePlanIndexChange?: (index: number) => void
  hideSectionTabs?: boolean
  mapControlsPlacement?: "top-left" | "bottom-left"
  mapControlsSize?: "sm" | "lg"
  // Optional table-state styling overrides for the highlight overlays; defaults preserve the
  // colours/opacities used by the staff layout editor and desktop reservation flow.
  freeTableColor?: string
  highlightOverlayOpacity?: number
  disabledTableOpacity?: number
  // "fade" (default) dims unavailable tables via opacity; "recolor" repaints them grey at full
  // opacity (opt-in for the mobile public map). Staff/desktop keep "fade".
  disabledTableTreatment?: "fade" | "recolor"
  bottomContent?: React.ReactNode
  fitPadding?: number
  frameless?: boolean
}

// Large plans are clamped so they never render below half of the width-fit scale;
// a readable portion of the plan beats an entire plan that is too small to see.
const MIN_WIDTH_FIT_RATIO = 0.5

const PlanViewer: React.FC<PlanViewerProps> = ({
  svgContent,
  venuePlans,
  tables,
  selectedTableId = null,
  highlightedTableId = null,
  selectedTableIds = [],
  highlightedTableIds = [],
  freeTableIds = [],
  disabledTables = {},
  onTableSelect,
  onClickAway,
  showSchedule = false,
  scheduleComponent,
  showTableLabels = false,
  showZoomInTabs = false,
  activePlanIndex,
  onActivePlanIndexChange,
  hideSectionTabs = false,
  mapControlsPlacement = "top-left",
  mapControlsSize = "sm",
  freeTableColor = "#4ade80",
  highlightOverlayOpacity = 0.2,
  disabledTableOpacity = 0.2,
  disabledTableTreatment = "fade",
  bottomContent,
  fitPadding = 0,
  frameless = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgWrapperRef = useRef<HTMLDivElement>(null)
  const baseScaleRef = useRef(1)
  const geometryRef = useRef<SvgIntrinsicGeometry | null>(null)

  const plansWithContent = useMemo(() => venuePlans?.filter((m) => m.plan) || [], [venuePlans])
  const defaultPlanIndex = useMemo(() => plansWithContent.findIndex((m) => m.default), [plansWithContent])

  const [state, dispatch] = useReducer(
    planViewerReducer,
    createInitialState(defaultPlanIndex >= 0 ? defaultPlanIndex : 0),
  )
  const { zoom, pan } = state
  const isSectionControlled = activePlanIndex != null
  const selectedPlanIndex = isSectionControlled ? activePlanIndex : state.selectedPlanIndex

  const setZoom = useCallback((newZoom: number) => dispatch({ type: "SET_ZOOM", zoom: newZoom }), [])
  const setPan = useCallback((newPan: { x: number; y: number }) => dispatch({ type: "SET_PAN", pan: newPan }), [])

  const performZoomAction = useCallback(
    (direction: "in" | "out") => {
      if (!svgWrapperRef.current) return
      const wrapper = svgWrapperRef.current.parentElement
      if (!wrapper) return
      const rect = wrapper.getBoundingClientRect()
      const centerX = rect.width / 2
      const centerY = rect.height / 2

      const step = 2
      const prevZoom = state.zoom
      const newZoom = direction === "in" ? Math.min(prevZoom + step, 400) : Math.max(prevZoom - step, 100)

      if (newZoom === prevZoom) return

      const scale = newZoom / prevZoom
      const prevPan = state.pan
      const newPan = {
        x: centerX - (centerX - prevPan.x) * scale,
        y: centerY - (centerY - prevPan.y) * scale,
      }
      const constrainedPan = constrainPan(
        newPan,
        newZoom,
        svgWrapperRef,
        containerRef,
        baseScaleRef.current,
        geometryRef.current,
      )

      dispatch({ type: "ZOOM_AND_PAN", zoom: newZoom, pan: constrainedPan })
    },
    [state.zoom, state.pan],
  )

  const handleZoomIn = useCallback(() => performZoomAction("in"), [performZoomAction])
  const handleZoomOut = useCallback(() => performZoomAction("out"), [performZoomAction])

  const disabledTableIds = useMemo(() => Object.keys(disabledTables), [disabledTables])

  const effectiveSelectedTableIds = useMemo(
    () => (selectedTableIds.length > 0 ? selectedTableIds : selectedTableId ? [selectedTableId] : []),
    [selectedTableIds, selectedTableId],
  )
  const effectiveHighlightedTableIds = useMemo(
    () => (highlightedTableIds.length > 0 ? highlightedTableIds : highlightedTableId ? [highlightedTableId] : []),
    [highlightedTableIds, highlightedTableId],
  )

  const currentPlan = plansWithContent[selectedPlanIndex]
  const effectiveSvgContent = useMemo(
    () => (venuePlans ? currentPlan?.plan || null : svgContent || null),
    [venuePlans, currentPlan?.plan, svgContent],
  )

  const recalculatePlanDimensions = useCallback(() => {
    if (!svgWrapperRef.current) return
    const geometry = geometryRef.current
    if (!geometry) return

    const wrapper = svgWrapperRef.current.parentElement
    if (!wrapper) return

    const containerRect = wrapper.getBoundingClientRect()
    const containerWidth = containerRect.width
    const containerHeight = containerRect.height
    if (containerWidth === 0 || containerHeight === 0) return

    const availableWidth = Math.max(containerWidth - fitPadding * 2, 1)
    const availableHeight = Math.max(containerHeight - fitPadding * 2, 1)

    const svgWidth = geometry.width
    const svgHeight = geometry.height

    const scaleX = availableWidth / svgWidth
    const scaleY = availableHeight / svgHeight
    const initialScale = Math.max(Math.min(scaleX, scaleY), scaleX * MIN_WIDTH_FIT_RATIO)

    baseScaleRef.current = initialScale

    const scaledWidth = svgWidth * initialScale
    const scaledHeight = svgHeight * initialScale

    const centerX = (containerWidth - scaledWidth) / 2
    const centerY = (containerHeight - scaledHeight) / 2

    dispatch({ type: "RESET", zoom: 100, pan: { x: centerX, y: centerY } })
  }, [fitPadding])

  const updateTableInteractivity = useCallback(
    (svgElement: SVGSVGElement) => {
      const tableElements = svgElement.querySelectorAll("[data-tabb-table]")
      tableElements.forEach((element) => {
        const tableId = element.getAttribute("data-tabb-table")
        const svgTableElement = element as SVGElement

        if (!onTableSelect || !tableId) {
          svgTableElement.style.cursor = ""
          return
        }

        svgTableElement.style.cursor = disabledTableIds.includes(tableId) ? "not-allowed" : "pointer"
      })
    },
    [onTableSelect, disabledTableIds],
  )

  useEffect(() => {
    if (!containerRef.current || !effectiveSvgContent || !svgWrapperRef.current) return

    const container = containerRef.current
    // SECURITY: venue plan SVGs are sanitized server-side by VenuePlans::NormalizeSvg
    // during layout updates before they are persisted. If raw user SVG input ever
    // starts reaching this component, sanitize it here before assigning innerHTML.
    container.innerHTML = effectiveSvgContent

    const geometry = normalizeSvgInContainer(container)
    geometryRef.current = geometry

    if (!geometry) {
      baseScaleRef.current = 1
      dispatch({ type: "RESET", zoom: 100, pan: { x: 0, y: 0 } })
      return
    }

    recalculatePlanDimensions()
  }, [effectiveSvgContent, recalculatePlanDimensions])

  useEffect(() => {
    if (!containerRef.current) return

    const svgElement = containerRef.current.querySelector("svg")
    if (!svgElement) return

    updateTableInteractivity(svgElement)
  }, [effectiveSvgContent, updateTableInteractivity])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleClick = (event: MouseEvent) => {
      const svgElement = container.querySelector("svg")
      const target = event.target

      if (!svgElement || !(target instanceof Element) || !svgElement.contains(target)) return

      let currentElement: Element | null = target
      while (currentElement && currentElement !== svgElement) {
        const tableId = currentElement.getAttribute("data-tabb-table")
        if (tableId) {
          event.stopPropagation()
          if (onTableSelect && !disabledTableIds.includes(tableId)) {
            onTableSelect(tableId, { ctrlKey: event.ctrlKey, metaKey: event.metaKey })
          }
          return
        }
        currentElement = currentElement.parentElement
      }

      if (onClickAway) onClickAway()
    }

    container.addEventListener("click", handleClick as EventListener)

    return () => {
      container.removeEventListener("click", handleClick as EventListener)
    }
  }, [onTableSelect, onClickAway, effectiveSvgContent, disabledTableIds])

  useEffect(() => {
    if (!svgWrapperRef.current) return
    const viewportElement = svgWrapperRef.current.parentElement
    if (!viewportElement) return

    const ro = new ResizeObserver(() => {
      recalculatePlanDimensions()
    })
    ro.observe(viewportElement)

    return () => {
      ro.disconnect()
    }
  }, [recalculatePlanDimensions])

  useEffect(() => {
    if (!containerRef.current) return

    const svgElement = containerRef.current.querySelector("svg")
    if (!svgElement) return

    const existingHighlights = svgElement.querySelectorAll("[data-tabb-highlight-overlay]")
    existingHighlights.forEach((highlight) => highlight.remove())

    const isRecolor = disabledTableTreatment === "recolor"
    const allTableElements = svgElement.querySelectorAll("[data-tabb-table]")
    allTableElements.forEach((element) => {
      ;(element as SVGElement).style.opacity = "1"
      // In recolor mode, clear any grey left from a prior render so a table that becomes available
      // again restores its original artwork (the free/selected overlays sit on top of it).
      if (isRecolor) paintTable(element, "", "")
    })

    disabledTableIds.forEach((tableId) => {
      const elementsWithTableId = svgElement.querySelectorAll(`[data-tabb-table="${tableId}"]`)
      elementsWithTableId.forEach((element) => {
        // Recolor = grey at full opacity (not a faded one); fade = the default dimmed artwork.
        if (isRecolor) paintTable(element, DISABLED_RECOLOR_FILL, DISABLED_RECOLOR_STROKE)
        else (element as SVGElement).style.opacity = String(disabledTableOpacity)

        const tooltipText = disabledTables[tableId]
        if (tooltipText) {
          const existingTitle = element.querySelector("title[data-tabb-tooltip]")
          if (existingTitle) existingTitle.remove()

          const titleElement = document.createElementNS("http://www.w3.org/2000/svg", "title")
          titleElement.setAttribute("data-tabb-tooltip", "true")
          titleElement.textContent = tooltipText
          element.insertBefore(titleElement, element.firstChild)
        }
      })
    })

    const createHighlight = (tableId: string, color: string) => {
      const elementsWithTableId = svgElement.querySelectorAll(`[data-tabb-table="${tableId}"]`)

      elementsWithTableId.forEach((element) => {
        const graphicsElement = element as SVGGraphicsElement

        try {
          const highlightClone = graphicsElement.cloneNode(true) as SVGGraphicsElement
          highlightClone.setAttribute("data-tabb-highlight-overlay", "true")
          highlightClone.style.pointerEvents = "none"
          highlightClone.style.fill = color
          highlightClone.style.stroke = color
          highlightClone.style.strokeWidth = "2"
          highlightClone.style.opacity = String(highlightOverlayOpacity)
          highlightClone.removeAttribute("data-tabb-table")
          highlightClone.removeAttribute("id")

          const allChildren = highlightClone.querySelectorAll("*")
          allChildren.forEach((child) => {
            ;(child as SVGElement).style.fill = color
            ;(child as SVGElement).style.stroke = color
          })

          const parent = graphicsElement.parentElement
          if (parent) {
            parent.insertBefore(highlightClone, graphicsElement.nextSibling)
          }
        } catch (e) {
          console.warn("Failed to create highlight overlay for element", e)
        }
      })
    }

    freeTableIds.forEach((tableId) => {
      if (!effectiveSelectedTableIds.includes(tableId) && !effectiveHighlightedTableIds.includes(tableId)) {
        createHighlight(tableId, freeTableColor)
      }
    })

    effectiveSelectedTableIds.forEach((tableId) => {
      createHighlight(tableId, PRIMARY_BLUE)
    })
    effectiveHighlightedTableIds.forEach((tableId) => {
      if (!effectiveSelectedTableIds.includes(tableId)) {
        createHighlight(tableId, "#f59e0b")
      }
    })
  }, [
    effectiveSelectedTableIds,
    effectiveHighlightedTableIds,
    freeTableIds,
    disabledTableIds,
    disabledTables,
    freeTableColor,
    highlightOverlayOpacity,
    disabledTableOpacity,
    disabledTableTreatment,
  ])

  useEffect(() => {
    if (!containerRef.current || !showTableLabels) return

    const svgElement = containerRef.current.querySelector("svg")
    if (!svgElement) return

    const existingLabels = svgElement.querySelectorAll("[data-tabb-table-label]")
    existingLabels.forEach((label) => label.remove())

    const tableNameMap = new Map<string, string>()
    tables.forEach((table) => {
      tableNameMap.set(table.id, table.name)
    })

    const tableElements = svgElement.querySelectorAll("[data-tabb-table]")
    const tableIdToElements = new Map<string, SVGGraphicsElement[]>()

    tableElements.forEach((element) => {
      const tableId = element.getAttribute("data-tabb-table")
      if (tableId) {
        if (!tableIdToElements.has(tableId)) {
          tableIdToElements.set(tableId, [])
        }
        tableIdToElements.get(tableId).push(element as SVGGraphicsElement)
      }
    })

    tableIdToElements.forEach((elements, tableId) => {
      const tableName = tableNameMap.get(tableId)
      if (!tableName) return

      try {
        let minX = Infinity
        let minY = Infinity
        let maxX = -Infinity
        let maxY = -Infinity

        elements.forEach((element) => {
          try {
            const bbox = element.getBBox()
            const ctm = element.getCTM()
            const svgCTM = svgElement.getCTM()

            if (ctm && svgCTM) {
              const svgCTMInverse = svgCTM.inverse()
              const transform = svgCTMInverse.multiply(ctm)

              const corners = [
                { x: bbox.x, y: bbox.y },
                { x: bbox.x + bbox.width, y: bbox.y },
                { x: bbox.x, y: bbox.y + bbox.height },
                { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
              ]

              corners.forEach((corner) => {
                const point = svgElement.createSVGPoint()
                point.x = corner.x
                point.y = corner.y
                const transformedPoint = point.matrixTransform(transform)
                minX = Math.min(minX, transformedPoint.x)
                minY = Math.min(minY, transformedPoint.y)
                maxX = Math.max(maxX, transformedPoint.x)
                maxY = Math.max(maxY, transformedPoint.y)
              })
            } else {
              minX = Math.min(minX, bbox.x)
              minY = Math.min(minY, bbox.y)
              maxX = Math.max(maxX, bbox.x + bbox.width)
              maxY = Math.max(maxY, bbox.y + bbox.height)
            }
          } catch (_e) {
            // getBBox can throw for elements not in the DOM
          }
        })

        if (minX === Infinity || minY === Infinity) return

        const centerX = (minX + maxX) / 2
        const centerY = (minY + maxY) / 2

        const labelGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
        labelGroup.setAttribute("data-tabb-table-label", tableId)
        labelGroup.style.pointerEvents = "none"

        const tempText = document.createElementNS("http://www.w3.org/2000/svg", "text")
        tempText.setAttribute("font-size", "10")
        tempText.setAttribute("font-family", "system-ui, -apple-system, sans-serif")
        tempText.textContent = tableName
        svgElement.appendChild(tempText)
        const textBBox = tempText.getBBox()
        svgElement.removeChild(tempText)

        const paddingX = 4
        const paddingY = 2

        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
        rect.setAttribute("x", String(centerX - textBBox.width / 2 - paddingX))
        rect.setAttribute("y", String(centerY - textBBox.height / 2 - paddingY))
        rect.setAttribute("width", String(textBBox.width + paddingX * 2))
        rect.setAttribute("height", String(textBBox.height + paddingY * 2))
        rect.setAttribute("rx", "4")
        rect.setAttribute("ry", "4")
        rect.setAttribute("fill", "white")
        rect.setAttribute("stroke", "#e5e7eb")
        rect.setAttribute("stroke-width", "1")

        const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
        text.setAttribute("x", String(centerX))
        text.setAttribute("y", String(centerY))
        text.setAttribute("text-anchor", "middle")
        text.setAttribute("dominant-baseline", "central")
        text.setAttribute("font-size", "10")
        text.setAttribute("font-family", "system-ui, -apple-system, sans-serif")
        text.setAttribute("fill", "#1f2937")
        text.textContent = tableName

        labelGroup.appendChild(rect)
        labelGroup.appendChild(text)
        svgElement.appendChild(labelGroup)
      } catch (e) {
        console.warn("Failed to create table label for", tableId, e)
      }
    })
  }, [effectiveSvgContent, tables, showTableLabels, selectedPlanIndex])

  if (!effectiveSvgContent) return null

  const renderPlanContainer = () => (
    <PlanContainer
      containerRef={containerRef}
      svgWrapperRef={svgWrapperRef}
      zoom={zoom}
      setZoom={setZoom}
      pan={pan}
      setPan={setPan}
      baseScale={baseScaleRef.current}
      geometry={geometryRef.current}
      hideZoomControls={showZoomInTabs}
      mapControlsPlacement={mapControlsPlacement}
      mapControlsSize={mapControlsSize}
      onResetView={recalculatePlanDimensions}
      frameless={frameless}
    />
  )

  const hasMultiplePlans = venuePlans && plansWithContent.length > 1
  const showBuiltInTabs = Boolean(hasMultiplePlans) && !hideSectionTabs

  if (!showBuiltInTabs) {
    return (
      <div className={STYLES.container} data-testid="plan-viewer-container">
        {showZoomInTabs ? (
          <div className={STYLES.zoomControlsBar} style={{ backgroundColor: "rgba(77,88,107,0.03)" }}>
            <PlanZoomControls zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          </div>
        ) : null}
        <div className={STYLES.planContent}>{renderPlanContainer()}</div>
        {showSchedule ? <div className={STYLES.flexShrink}>{scheduleComponent}</div> : null}
        {bottomContent ? <div className={STYLES.flexShrink}>{bottomContent}</div> : null}
      </div>
    )
  }

  return (
    <div className={STYLES.containerMultiPlan} data-testid="plan-viewer-container">
      <div
        role="tablist"
        aria-label="Plan sections"
        className={STYLES.tabList}
        style={{ backgroundColor: "rgba(77,88,107,0.03)" }}
      >
        <div className={STYLES.tabGroup}>
          {plansWithContent.map((venuePlan, index) => (
            <button
              key={venuePlan.id}
              type="button"
              role="tab"
              aria-selected={selectedPlanIndex === index}
              onClick={() =>
                isSectionControlled
                  ? onActivePlanIndexChange?.(index)
                  : dispatch({ type: "SET_SELECTED_PLAN_INDEX", index })
              }
              className={`${STYLES.tabButton} ${
                selectedPlanIndex === index ? "text-gray-700 font-medium" : "text-gray-500 hover:text-gray-700"
              }`}
              style={{
                borderBottom: selectedPlanIndex === index ? `2px solid ${PRIMARY_BLUE}` : "2px solid transparent",
              }}
              data-testid={`plan-viewer-tab-${venuePlan.id}`}
            >
              {venuePlan.sectionName}
            </button>
          ))}
        </div>
        {showZoomInTabs ? <PlanZoomControls zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} /> : null}
      </div>
      <div className={STYLES.planContent}>{renderPlanContainer()}</div>
      {showSchedule ? <div className={STYLES.flexShrink}>{scheduleComponent}</div> : null}
      {bottomContent ? <div className={STYLES.flexShrink}>{bottomContent}</div> : null}
    </div>
  )
}

export default PlanViewer
